import {
  forwardRef,
  ReactNode,
  cloneElement,
  ComponentPropsWithoutRef,
  ReactElement,
  useId,
} from 'react'
import { IconAlertTriangle, IconCircleCheckFilled } from '@tabler/icons-react'
import { FieldError } from 'react-hook-form'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from 'utils/classMerge'

import {
  FieldWrapper,
  FieldWrapperPassThroughProps,
} from '../form/field-wrapper'
import { getFontClass } from '../utils/get-font-class'
import Text from '../text'

const inputVariants = cva(
  `
    peer
    tw-flex tw-w-full
    tw-rounded-md
    tw-border tw-border-solid
    tw-bg-white
    tw-font-[roboto]
    tw-shadow-sm
    tw-ring-0
    tw-transition-colors
    placeholder:tw-text-zinc-400
    focus:tw-outline-none
    disabled:tw-cursor-not-allowed disabled:tw-border-zinc-300 disabled:tw-bg-zinc-50 disabled:tw-text-zinc-400
  `,
  {
    variants: {
      size: {
        default: 'tw-h-9 tw-px-3 tw-py-2 tw-text-sm',
        lg: 'tw-h-10 tw-px-3 tw-py-2.5 tw-text-sm',
      },
      withError: {
        true: `
          tw-border-rose-600
          focus:tw-border-transparent focus:tw-ring-2 focus:tw-ring-rose-600
        `,
        false: `
          tw-border-zinc-300
          focus:tw-border-transparent focus:tw-ring-2 focus:tw-ring-orange-500
        `,
      },
      withIcon: {
        true: 'tw-pl-10',
        false: '',
      },
    },
    defaultVariants: {
      size: 'default',
      withError: false,
      withIcon: false,
    },
  },
)

type InputVariantProps = Omit<VariantProps<typeof inputVariants>, 'size'> & {
  size?: 'default' | 'lg'
}

type InputBaseProps = ComponentPropsWithoutRef<'input'>

interface InputProps
  extends Omit<InputBaseProps, 'size'>,
    FieldWrapperPassThroughProps,
    InputVariantProps {
  /** Label text displayed above the input */
  label?: string
  /** Helper text displayed below the input */
  helperText?: string
  /** Shows success state with green styling */
  success?: boolean
  /** Custom icon to display */
  icon?: ReactNode
  /** Additional className for the wrapper div */
  wrapperClassName?: string
  /** Preserve custom icon even when there are error/success states */
  preserveIcon?: boolean
  /** Error state from react-hook-form */
  error?: FieldError
}

interface InputIconProps {
  error?: FieldError
  success?: boolean
  icon?: ReactNode
  preserveIcon?: boolean
}

const InputIcon = ({ error, success, icon, preserveIcon }: InputIconProps) => {
  const iconBaseClasses = `
    tw-absolute
    tw-left-3
    tw-top-1/2
    -tw-translate-y-1/2
    tw-text-zinc-400
    peer-focus:tw-text-orange-500
    tw-size-5
  `

  if (error && !preserveIcon) {
    return (
      <IconAlertTriangle
        className={cn(iconBaseClasses, 'tw-stroke-rose-600')}
        aria-hidden="true"
        data-testid="error-icon"
      />
    )
  }

  if (success && !preserveIcon) {
    return (
      <IconCircleCheckFilled
        className={cn(iconBaseClasses, 'tw-text-emerald-400')}
        aria-hidden="true"
        data-testid="success-icon"
      />
    )
  }

  if (icon) {
    return cloneElement(icon as ReactElement, {
      className: cn(iconBaseClasses, (icon as ReactElement).props.className),
      'aria-hidden': true,
      'data-testid': 'input-custom-icon',
    })
  }

  return null
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      helperText,
      error,
      success,
      icon,
      wrapperClassName,
      'aria-label': ariaLabel,
      id: providedId,
      preserveIcon = false,
      size,
      ...props
    },
    ref,
  ) => {
    const fallbackId = useId()
    const id = providedId ?? fallbackId
    const fontClass = getFontClass(className)

    return (
      <FieldWrapper
        className={cn('tw-w-full tw-text-left', fontClass, wrapperClassName)}
        label={label}
        error={error}
      >
        <div className="tw-relative">
          <InputIcon
            error={error}
            success={success}
            icon={icon}
            preserveIcon={preserveIcon}
          />

          <input
            id={id}
            className={cn(
              inputVariants({
                size,
                withError: Boolean(error),
                withIcon: Boolean(icon || error || success),
                className,
              }),
            )}
            data-testid="input-element"
            aria-label={ariaLabel || label}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={helperText ? `${id}-helper` : undefined}
            ref={ref}
            {...props}
          />
        </div>

        {helperText && !error && (
          <Text
            as="p"
            id={`${id}-helper`}
            className={cn(
              'tw-mt-1 tw-text-xs',
              error ? 'tw-text-rose-600' : 'tw-text-zinc-500',
            )}
          >
            {helperText}
          </Text>
        )}
      </FieldWrapper>
    )
  },
)

Input.displayName = 'Input'

export default Input
export type { InputProps }
export { inputVariants }
